import {
  GraphQLObjectType,
  GraphQLString,
  GraphQLID,
  GraphQLBoolean,
  GraphQLNonNull,
  GraphQLList,
  GraphQLInt
} from 'graphql'
import { AttackStatusType, AttackStatus } from './attack-status'
import {
  vulnerabilitySeverityBreakdown,
  VulnerabilitySeverityBreakdown
} from './vulnerability-severity-breakdown'
import { License, license } from './license'
import { Role, role } from './role'
import { GradeValue, GradeValueType } from './grade-value'
import {
  ApplicationImportanceType,
  ApplicationImportance
} from './application-importance'
import {
  BaseCompliancePolicy,
  BaseCompliancePolicyType
} from './compliance-policy'
import { MetadataValue, metadataValueType } from './metadata-value'
import {
  InvalidCustomFieldType,
  invalidCustomFieldType
} from './invalid-custom-field'

export interface BaseApplication {
  readonly id: string
  readonly name: string
}

export const baseApplication = new GraphQLObjectType({
  name: 'BaseApplication',
  fields: () => ({
    id: {
      description: 'Application ID',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Application name',
      type: new GraphQLNonNull(GraphQLString)
    }
  })
})

export interface Application extends BaseApplication {
  readonly language: string
  readonly vulnerabilityBreakdown: VulnerabilitySeverityBreakdown
  readonly archived: boolean
  readonly attackStatus?: AttackStatus
  readonly attackTotal: number
  readonly tags: string[]
  readonly hasAssess: boolean
  readonly hasAProtectedServer: boolean
  readonly license: License | null
  readonly role?: Role
  readonly url: string
  readonly linesOfCode: string
  readonly importance: ApplicationImportance
  readonly technologies: string[]
  readonly online: boolean
  readonly lastActivity: string
  readonly primary: boolean
  readonly moduleCount: number
  readonly violatedCompliancePolicies: BaseCompliancePolicy[]
  readonly customMetadataFields: MetadataValue[]
  readonly invalidCustomMetadataFields: InvalidCustomFieldType[]
  readonly scoreGrade: GradeValue
}

export const ApplicationType = new GraphQLObjectType({
  name: 'Application',
  fields: () => ({
    id: {
      description: 'The unique ID for an application',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Human readable name for the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    language: {
      description:
        'The programming language in which the application was written',
      type: new GraphQLNonNull(GraphQLString)
    },
    vulnerabilityBreakdown: {
      description: 'Vulnerability breakdown by severity',
      type: new GraphQLNonNull(vulnerabilitySeverityBreakdown)
    },
    archived: {
      description: 'The archived status of the application',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    attackStatus: {
      description: 'The current attack status',
      type: AttackStatusType
    },
    attackTotal: {
      description: 'The current number of active attacks',
      type: new GraphQLNonNull(GraphQLInt)
    },
    tags: {
      description: 'User defined tags',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    hasAssess: {
      description: 'Indicates if the application has Assess feature enabled.',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    hasAProtectedServer: {
      description:
        'Indicates if the application has at least one protected server.',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    license: {
      description: 'Application licensing information',
      type: license
    },
    role: {
      description:
        "The current user's most permissive role for this application",
      type: role
    },
    url: {
      description: 'Application path',
      type: new GraphQLNonNull(GraphQLString)
    },
    linesOfCode: {
      description: 'Total lines of code',
      type: new GraphQLNonNull(GraphQLString)
    },
    importance: {
      description: 'The application importance',
      type: new GraphQLNonNull(ApplicationImportanceType)
    },
    technologies: {
      description: 'List of technologies',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    online: {
      description: 'Is the application currently online',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    lastActivity: {
      description: 'Timestamp of the last activity for this application',
      type: new GraphQLNonNull(GraphQLString)
    },
    primary: {
      description:
        'Is the application the primary of a group of merged applications',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    moduleCount: {
      description:
        'The number of application modules represented by this application',
      type: new GraphQLNonNull(GraphQLInt)
    },
    violatedCompliancePolicies: {
      description:
        'A collection of compliance policies that this application violates',
      type: new GraphQLList(new GraphQLNonNull(BaseCompliancePolicyType))
    },
    customMetadataFields: {
      description:
        'A collection of custom metadata fields that this application has configured',
      type: new GraphQLList(new GraphQLNonNull(metadataValueType))
    },
    invalidCustomMetadataFields: {
      description: 'A list of field IDs that currently have validation errors',
      type: new GraphQLList(new GraphQLNonNull(invalidCustomFieldType))
    },
    scoreGrade: {
      description: 'A letter grade used to score the application',
      type: new GraphQLNonNull(GradeValueType)
    }
  })
})
